import type {getAllStudents} from "./pages/student_list/server"
import type {getAllEnrollments} from "./pages/enrollments_filters_advanced/server"
import type {deleteFilter} from "./pages/enrollments_filters_advanced/server"
import type {getFiltersForType} from "./pages/enrollments_filters_advanced/server"
import type {saveFilter} from "./pages/enrollments_filters_advanced/server"
import type { cancelNoReenrollment } from "./pages/reenrollments/server";
import type { makeStudentChargeBatch } from "./pages/student_charges/server";
import type { makeMutualityCertificates } from "./pages/lessongroup_mutuality_certificates/server";
import type { downloadMutualityCertificate } from "./pages/lessongroup_mutuality_certificates/server";
import type { searchCampEnrollments } from "./pages/mutuality_certificate_config/server";
import type { simulateMutualityCertificateConfig } from "./pages/mutuality_certificate_config/server";
import type { saveMutualityCertificateConfig } from "./pages/mutuality_certificate_config/server";
import type { saveStudentLinks } from "./pages/config_student_links/server";
import type { saveTeacherLinks } from "./pages/config_teacher_links/server";
import type { queryPaymentStatistics } from "./pages/finance_payments_overview/server";
import type { savePublicEnrollmentsConfig } from "./pages/config_public_enrollments/server";
import * as joda from "@js-joda/core";
import * as c from "purify-ts/Codec";
import { Either, Left, Right } from "purify-ts/Either";
import {
  deserializeProps,
  runWithCustomSerializers,
} from "trader.ts/clientcomponents/serialization";
import {
  Encoder,
  builtinEncoders,
  makeEncoderFromCodec,
  makeEncoderFromSum,
  makeRoute,
} from "trader.ts/route";
import { apiSpec } from "trader.ts/router";
import { tryExtractErrorMessage } from "trader.ts/utils";
import { mollie_payment_method } from "../mollie";
import {
  b2fileid,
  emailid,
  enrollmentid,
  enrollmentrequestid,
  evaluation_template_id,
  evaluationid,
  eventid,
  expectedpaymentid,
  filenoteid,
  formulaid,
  image_id,
  instrumentid,
  internalteacherid,
  invoiceid,
  lessongroupid,
  lessonid,
  messageid,
  messagetemplateid,
  messagetemplatetype,
  mollie_payment_id,
  payment_scheme_id,
  periodid,
  queuedmessageid,
  roomid,
  siteid,
  student_attachment_id,
  studentid,
  teacherid,
  tenantinvoiceid,
  thirdpartysellerid,
} from "../sql/classy/types";
import {
  doChangeLessongroup,
  getFormulaChangeActions,
} from "./domain/changelessongroup";
import type { getPeriodfeeForStudent } from "./domain/finance";
import { isString } from "./lib/istype";
import { parseIntSafe } from "./lib/parseintsafe";
import type {
  clearAbsencesToApproveList,
  removeAbsenceFromList,
} from "./pages/absences_to_approve_list/server";
import type {
  deleteAdminAccount,
  saveNewAdminAccount,
} from "./pages/adminaccounts/server";
import type {
  approveEnrollmentRequestWithLg,
  approvePrivateLessonEnrollmentRequest,
  disproveEnrollmentRequest,
  getPossibleTeachersForEnrollmentRequest,
} from "./pages/approve_enrollment_request/server";
import type { searchAuditLogOnStudent } from "./pages/audit_log_overview/server";
import type {
  findEvaluation,
  saveEvaluationPdfConfig,
} from "./pages/config_evaluation_pdf/server";
import type { saveEachPeriodFinancialConfig } from "./pages/config_financial/server";
import type {
  deleteFormula,
  saveFormula,
} from "./pages/config_formulas/server";
import type {
  deleteInstrument,
  saveInstrument,
} from "./pages/config_instruments/server";
import type {
  saveInvoicePdfConfig,
  searchInvoices,
} from "./pages/config_invoice_pdf/server";
import type {
  saveAlwaysShowWireInstructions,
  saveMollieProfile,
  saveMoneybirdAdministration,
} from "./pages/config_mollie/server";
import type { saveSchoolConfig } from "./pages/config_schoolconfig/server";
import type { doDeleteStudent } from "./pages/deletestudent/server";
import type {
  deleteEmailTemplate,
  emailTemplateSave,
} from "./pages/editemailtemplates/server";
import type { deleteEvent, saveEvent } from "./pages/editevent/server";
import type { saveStudentProfilesMessage } from "./pages/editmessageonstudentprofiles/server";
import type { saveEmailSettings } from "./pages/emailsettings/server";
import type {
  removeEmail,
  removeSms,
  resendEmail,
} from "./pages/erroremails/server";
import type {
  deleteEvaluation,
  finalizeEvaluation,
  saveEvaluation,
  unlockEvaluation,
} from "./pages/evaluation_edit/server";
import type { queryEvaluations } from "./pages/evaluation_list/server";
import type { queryEvaluationsMissing } from "./pages/evaluation_missing_list/server";
import type { saveEvaluationTemplate } from "./pages/evaluation_template/server";
import type { queryInvoiceStatistics } from "./pages/finance_invoices/server";
import type {
  getFilteredStudents,
  sendGroupMessage,
} from "./pages/groupmessage/server";
import type {
  markPaymentPaid,
  saveInvoiceFull,
  switchPaymentScheme,
} from "./pages/invoicedetails/server";
import type { queryInvoicesToReimburse } from "./pages/invoices_to_reimburse/server";
import type { getLessonStatistic } from "./pages/lesson_statistics/server";
import type {
  deleteLesson,
  getTeacherById,
  rollbackStudentAbsenceReplacementLesson,
  saveNewLesson,
  saveStudentAbsenceComments,
} from "./pages/lessongroup_lessondata/server";
import type { updateQuestions } from "./pages/lessongroup_questions_edit/server";
import type { tryPasswordlessLogin } from "./pages/login/passwordless";
import type { tryWithPasswordLogin } from "./pages/login/withpassword";
import type {
  messagequeueUpdate,
  sendEmailFromMessageQueueCustom,
} from "./pages/messagequeue/server";
import { counterpartyC } from "./pages/messages/client";
import type {
  getMessagesForConversation,
  markAsUnread,
  searchCounterparty,
  sendMessage,
} from "./pages/messages/server";
import type {
  deleteFileNote,
  deleteTextNote,
  getNotesForLessongroup,
  saveFileNote,
  saveTextNote,
} from "./pages/notes/server";
import type { getMolliePaymentMethods } from "./pages/payinvoice/server";
import type {
  getPossibleInvoicesForOnlinePayment,
  saveOnlinePaymentFull,
} from "./pages/payment_detail/server";
import type { savePaymentScheme } from "./pages/payment_schemes/server";
import type {
  queryNonPeriodicPaymentsStatistic,
  sendPaymentRemindersNonPeriodic,
} from "./pages/payments_overview_non_periodic/server";
import type {
  queryPeriodPaymentsStatistic,
  sendPaymentRemindersAndDirectDebitsPeriodic,
} from "./pages/payments_overview_periodic/server";
import type { savePeriod } from "./pages/periodsconfig/server";
import type {
  calculateCancelTeacherabsenceActions,
  calculateRoomSuggestions,
  cancelTeacherAbsence,
  deleteTeachingPeriod,
  getActiveLessonsForLessongroup,
  getAllEnrollmentsForLessongroup,
  getAvailableRoomsForTeachingPeriod,
  getCatchupLessonDates,
  getComplementLessonsBySlot,
  getInvoiceLinesForLessongroupHandler,
  getNonPeriodicPaymentScheme,
  getPossibleExternalReplacements,
  getPossibleInternalReplacements,
  getStoppingEnrollmentPeriodfeeStatus,
  loadSlots,
  moveEnrollment,
  moveLessonOrLessongroup,
  saveNewEnrollment,
  saveNewGrouplesson,
  saveNewPrivateLessongroup,
  saveTeacherAbsence,
  saveTeachingperiod,
  searchStudent,
  setPlanningDisplayMode,
  setPlanningFilter,
  stopEnrollment,
  stopLessongroup,
  stopPrivateLessongroup,
} from "./pages/planning/server";
import type {
  getStudentFinancialDetails,
  saveNewPublicEnrollment,
} from "./pages/public_new_enrollment/server";
import type {
  confirmReenrollment,
  rejectReenrollment,
} from "./pages/reenroll_invitation/server";
import type {
  execReenrollments,
  queryReenrollments,
} from "./pages/reenrollments/server";
import type { saveNewPassword } from "./pages/reset_password/server";
import type { deleteRoom, saveRoom } from "./pages/roomedit/server";
import type {
  loadRoomplanningSlots,
  setRoomplanningFilterCookie,
} from "./pages/roomplanning/server";
import type { doSearch } from "./pages/search/server";
import type { deleteSite, saveSite } from "./pages/site_edit/server";
import type { loadEnrollmentStatistics } from "./pages/statistics_enrollments/server";
import type { saveStudentDirectDebitInfo } from "./pages/student_direct_debit_mandates/server";
import type { mergeStudent } from "./pages/student_merge/server";
import type { saveStudentProfile } from "./pages/student_profile/server";
import type { getEnrollmentDetailsForStudentStart } from "./pages/student_start/server";
import type {
  saveStudentAbsence,
  uploadStudentAbsenceFile,
} from "./pages/student_start_absences/server";
import type { saveStudent } from "./pages/studentedit/server";
import type {
  deleteStudentAttachment,
  saveStudentAttachment,
} from "./pages/studentoverview/server";
import type {
  saveTeacher,
  setTeacherActive,
} from "./pages/teacher_edit/server";
import type {
  getNotesForLesson,
  setStudentPresence,
} from "./pages/teacher_lessonroster/server";
import type {
  deleteThirdPartySeller,
  saveThirdPartySeller,
} from "./pages/third_party_seller_edit/server";
import type { reporterror } from "./pages/uncaughterror/server";
import type { deleteFromWaitinglist } from "./pages/waitinglist_admin/server";
import type { deleteFromWaitinglistLessongroup } from "./pages/waitinglist_admin_lessongroup/server";
import type {
  saveWaitinglistExistingStudent,
  saveWaitinglistNewStudent,
} from "./pages/waitinglist_student/server";
import { HHmm } from "./render";
import { role, roleToString, stringToRole } from "./role";

const timeEncoder: Encoder<joda.LocalTime> = {
  parse: (s: string) => {
    try {
      return Right(joda.LocalTime.parse(s, HHmm));
    } catch (err) {
      return Left(new Error(tryExtractErrorMessage(err)));
    }
  },
  serialize: (t) =>
    t.hour().toString().padStart(2, "0") +
    ":" +
    t.minute().toString().padStart(2, "0"),
};

const newtypeEncoders = {
  b2fileid: newtypeEncoderString<b2fileid>(),
  emailid: newtypeEncoder<emailid>(),
  enrollmentid: newtypeEncoder<enrollmentid>(),
  enrollmentrequestid: newtypeEncoder<enrollmentrequestid>(),
  evaluation_template_id: newtypeEncoder<evaluation_template_id>(),
  evaluationid: newtypeEncoder<evaluationid>(),
  eventid: newtypeEncoder<eventid>(),
  expectedpaymentid: newtypeEncoder<expectedpaymentid>(),
  filenoteid: newtypeEncoder<filenoteid>(),
  formulaid: newtypeEncoder<formulaid>(),
  image_id: newtypeEncoder<image_id>(),
  instrumentid: newtypeEncoder<instrumentid>(),
  internalteacherid: newtypeEncoder<internalteacherid>(),
  invoiceid: newtypeEncoder<invoiceid>(),
  lessongroupid: newtypeEncoder<lessongroupid>(),
  lessonid: newtypeEncoder<lessonid>(),
  messageid: newtypeEncoder<messageid>(),
  messagetemplateid: newtypeEncoder<messagetemplateid>(),
  messagetemplatetype: newtypeEncoderString<messagetemplatetype>(),
  mollie_payment_id: newtypeEncoderString<mollie_payment_id>(),
  payment_scheme_id: newtypeEncoder<payment_scheme_id>(),
  periodid: newtypeEncoder<periodid>(),
  queuedmessageid: newtypeEncoder<queuedmessageid>(),
  roomid: newtypeEncoder<roomid>(),
  siteid: newtypeEncoder<siteid>(),
  student_attachment_id: newtypeEncoder<student_attachment_id>(),
  studentid: newtypeEncoder<studentid>(),
  teacherid: newtypeEncoder<teacherid>(),
  tenantinvoiceid: newtypeEncoder<tenantinvoiceid>(),
  thirdpartysellerid: newtypeEncoder<thirdpartysellerid>(),
};

export const startRoute = makeRoute("/");

export const viaDemoRoute = makeRoute(
  "/viademo/{email:string}/{displayname:string}/{reset:number}",
);

export const imageRoute = makeRoute(
  "/image/{image_id:image_id}",
  newtypeEncoders,
);

export const lessonsStatisticsRoute = makeRoute("/Statistics/lessons");
export const enrollmentsStatisticsRoute = makeRoute("/Statistics/enrollments");

export const planningStartPublicRoute = makeRoute("/ts/planning_start_public");

export const planningPublicByTeachername = makeRoute(
  "/ts/planning_start_public/teachername/{teachername:string}",
);

export const planningPublicByInstrumentname = makeRoute(
  "/ts/planning_start_public/instrumentname/{instrumentname:string}",
);

export const messagequeueRoute = makeRoute("/ts/messagequeue");
export const messagequeueUpdateRoute = apiSpec({
  route: makeRoute("/ts/messagequeue"),
  method: "POST",
  body: magicCodec<Parameters<typeof messagequeueUpdate>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof messagequeueUpdate>>>(),
});

export const messagequeueDetailRoute = makeRoute(
  "/ts/messagequeue/detail/{identifier:queuedmessageid}?{messagetemplateid:messagetemplateid}",
  newtypeEncoders,
);

export const editemailtemplates_listRoute = makeRoute(
  "/ts/editemailtemplates_list",
);
export const editemailtemplatesRoute = makeRoute(
  "/ts/editemailtemplates/{templatetype:messagetemplatetype}?{id:messagetemplateid}",
  newtypeEncoders,
);

export const emailTemplateSaveR = apiSpec({
  route: makeRoute("/ts/emailtemplates/save"),
  method: "POST",
  body: magicCodec<Parameters<typeof emailTemplateSave>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof emailTemplateSave>>>(),
});
//$PLACEHOLDER1

export const getAllStudentsSpec = apiSpec({
  route: makeRoute("/ts/getallstudents"),
  method: "POST",
	body: magicCodec<Parameters<typeof getAllStudents>[2]>(),
	returns: magicCodec<Awaited<ReturnType<typeof getAllStudents>>>(),
});

export const getAllEnrollmentsSpec = apiSpec({
  route: makeRoute("/ts/getallenrollments"),
  method: "POST",
	body: magicCodec<Parameters<typeof getAllEnrollments>[2]>(),
	returns: magicCodec<Awaited<ReturnType<typeof getAllEnrollments>>>(),
});

export const deleteFilterSpec = apiSpec({
  route: makeRoute("/ts/deletefilter"),
  method: "POST",
	body: magicCodec<Parameters<typeof deleteFilter>[2]>(),
	returns: magicCodec<Awaited<ReturnType<typeof deleteFilter>>>(),
});

export const getFiltersForTypeSpec = apiSpec({
  route: makeRoute("/ts/getfiltersfortype"),
  method: "POST",
	body: magicCodec<Parameters<typeof getFiltersForType>[2]>(),
	returns: magicCodec<Awaited<ReturnType<typeof getFiltersForType>>>(),
});

export const saveFilterSpec = apiSpec({
  route: makeRoute("/ts/savefilter"),
  method: "POST",
	body: magicCodec<Parameters<typeof saveFilter>[2]>(),
	returns: magicCodec<Awaited<ReturnType<typeof saveFilter>>>(),
});
export const enrollments_filters_advancedRoute = makeRoute("/ts/enrollments_filters_advanced", newtypeEncoders);

export const cancelNoReenrollmentSpec = apiSpec({
  route: makeRoute("/ts/cancelnoreenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof cancelNoReenrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof cancelNoReenrollment>>>(),
});
export const admin_dashboardRoute = makeRoute(
  "/ts/admin_dashboard",
  newtypeEncoders,
);

export const makeStudentChargeBatchSpec = apiSpec({
  route: makeRoute("/ts/makestudentchargebatch"),
  method: "POST",
  body: magicCodec<Parameters<typeof makeStudentChargeBatch>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof makeStudentChargeBatch>>>(),
});
export const student_chargesRoute = makeRoute(
  "/ts/student_charges",
  newtypeEncoders,
);

export const makeMutualityCertificatesSpec = apiSpec({
  route: makeRoute("/ts/makemutualitycertificates"),
  method: "POST",
  body: magicCodec<Parameters<typeof makeMutualityCertificates>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof makeMutualityCertificates>>>(),
});

export const downloadMutualityCertificateSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadmutualitycertificate/{enrollmentid:enrollmentid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});
export const lessongroup_mutuality_certificatesRoute = makeRoute(
  "/ts/lessongroup_mutuality_certificates/{lessongroupid:lessongroupid}",
  newtypeEncoders,
);

export const searchCampEnrollmentsSpec = apiSpec({
  route: makeRoute("/ts/searchcampenrollments"),
  method: "POST",
  body: magicCodec<Parameters<typeof searchCampEnrollments>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof searchCampEnrollments>>>(),
});

export const simulateMutualityCertificateConfigSpec = apiSpec({
  route: makeRoute(
    "/ts/simulatemutualitycertificateconfig/{enrollmentid:enrollmentid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});

export const saveMutualityCertificateConfigSpec = apiSpec({
  route: makeRoute("/ts/savemutualitycertificateconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveMutualityCertificateConfig>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof saveMutualityCertificateConfig>>>(),
});
export const mutuality_certificate_configRoute = makeRoute(
  "/ts/mutuality_certificate_config",
  newtypeEncoders,
);
export const student_start_linksRoute = makeRoute(
  "/ts/student_start_links",
  newtypeEncoders,
);

export const saveStudentLinksSpec = apiSpec({
  route: makeRoute("/ts/savestudentlinks"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentLinks>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentLinks>>>(),
});
export const config_student_linksRoute = makeRoute(
  "/ts/config_student_links",
  newtypeEncoders,
);

export const saveTeacherLinksSpec = apiSpec({
  route: makeRoute("/ts/saveteacherlinks"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveTeacherLinks>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveTeacherLinks>>>(),
});
export const config_teacher_linksRoute = makeRoute(
  "/ts/config_teacher_links",
  newtypeEncoders,
);
export const config_overviewRoute = makeRoute(
  "/ts/config_overview",
  newtypeEncoders,
);

export const queryPaymentStatisticsSpec = apiSpec({
  route: makeRoute("/ts/querypaymentstatistics"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryPaymentStatistics>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryPaymentStatistics>>>(),
});
export const finance_payments_overviewRoute = makeRoute(
  "/ts/finance_payments_overview",
  newtypeEncoders,
);

export const savePublicEnrollmentsConfigSpec = apiSpec({
  route: makeRoute("/ts/savepublicenrollmentsconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof savePublicEnrollmentsConfig>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof savePublicEnrollmentsConfig>>>(),
});
export const config_public_enrollmentsRoute = makeRoute(
  "/ts/config_public_enrollments",
  newtypeEncoders,
);
export const third_party_seller_studentsRoute = makeRoute(
  "/ts/third_party_seller_students/{thirdpartysellerid:thirdpartysellerid}",
  newtypeEncoders,
);

export const getStudentFinancialDetailsSpec = apiSpec({
  route: makeRoute("/ts/getstudentfinancialdetails"),
  method: "POST",
  body: magicCodec<Parameters<typeof getStudentFinancialDetails>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getStudentFinancialDetails>>>(),
});
export const student_start_messages_historyRoute = makeRoute(
  "/ts/student_start_messages_history?{target:target}",
  {
    target: makeEncoderFromCodec(
      c.Codec.interface({
        own_identity: counterpartyC,
        counterparty: counterpartyC,
      }),
    ),
  },
);
export const student_start_messages_new_messageRoute = makeRoute(
  "/ts/student_start_messages/new_message",
  newtypeEncoders,
);
export const student_start_messagesRoute = makeRoute(
  "/ts/student_start_messages",
  newtypeEncoders,
);
export const student_start_evaluationsRoute = makeRoute(
  "/ts/student_start_evaluations/{lessongroupid:lessongroupid}/{enrollmentid:enrollmentid}",
  newtypeEncoders,
);

export const saveStudentAbsenceSpec = apiSpec({
  route: makeRoute("/ts/savestudentabsence"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentAbsence>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentAbsence>>>(),
});
export const student_start_absencesRoute = makeRoute(
  "/ts/student_start_absences/{lessongroupid:lessongroupid}/{enrollmentid:enrollmentid}",
  newtypeEncoders,
);
export const student_start_lessons_notesRoute = makeRoute(
  "/ts/student_start_lessons_notes/{lessongroupid:lessongroupid}/{enrollmentid:enrollmentid}",
  newtypeEncoders,
);

export const sendEmailFromMessageQueueCustomSpec = apiSpec({
  route: makeRoute("/ts/sendemailfrommessagequeuecustom"),
  method: "POST",
  body: magicCodec<Parameters<typeof sendEmailFromMessageQueueCustom>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof sendEmailFromMessageQueueCustom>>>(),
});

export const deleteThirdPartySellerSpec = apiSpec({
  route: makeRoute("/ts/deletethirdpartyseller"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteThirdPartySeller>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteThirdPartySeller>>>(),
});

export const saveThirdPartySellerSpec = apiSpec({
  route: makeRoute("/ts/savethirdpartyseller"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveThirdPartySeller>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveThirdPartySeller>>>(),
});
export const third_party_seller_editRoute = makeRoute(
  "/ts/third_party_seller_edit?{third_party_seller_id:thirdpartysellerid}",
  newtypeEncoders,
);
export const third_party_sellersRoute = makeRoute("/ts/third_party_sellers");

export const getMolliePaymentMethodsSpec = apiSpec({
  route: makeRoute("/ts/getmolliepaymentmethods"),
  method: "POST",
  body: magicCodec<Parameters<typeof getMolliePaymentMethods>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getMolliePaymentMethods>>>(),
});

export const mergeStudentSpec = apiSpec({
  route: makeRoute("/ts/mergestudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof mergeStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof mergeStudent>>>(),
});
export const student_mergeRoute = makeRoute(
  "/ts/student_merge/{studentid:studentid}",
  newtypeEncoders,
);

export const saveStudentDirectDebitInfoSpec = apiSpec({
  route: makeRoute("/ts/savestudentdirectdebitinfo"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentDirectDebitInfo>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentDirectDebitInfo>>>(),
});
export const student_direct_debit_mandatesRoute = makeRoute(
  "/ts/student_direct_debit_mandates",
);

export const clearAbsencesToApproveListSpec = apiSpec({
  route: makeRoute("/ts/clearabsencestoapprovelist"),
  method: "POST",
  body: magicCodec<Parameters<typeof clearAbsencesToApproveList>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof clearAbsencesToApproveList>>>(),
});

export const unlockEvaluationSpec = apiSpec({
  route: makeRoute("/ts/unlockevaluation"),
  method: "POST",
  body: magicCodec<Parameters<typeof unlockEvaluation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof unlockEvaluation>>>(),
});

export const searchInvoicesSpec = apiSpec({
  route: makeRoute("/ts/search_invoices"),
  method: "POST",
  body: magicCodec<Parameters<typeof searchInvoices>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof searchInvoices>>>(),
});

export const saveInvoicePdfConfigSpec = apiSpec({
  route: makeRoute("/ts/saveinvoicepdfconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveInvoicePdfConfig>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveInvoicePdfConfig>>>(),
});
export const config_invoice_pdfRoute = makeRoute("/ts/config_invoice_pdf");

export const findEvaluationSpec = apiSpec({
  route: makeRoute("/ts/findevaluation"),
  method: "POST",
  body: magicCodec<Parameters<typeof findEvaluation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof findEvaluation>>>(),
});

export const saveEvaluationPdfConfigSpec = apiSpec({
  route: makeRoute("/ts/saveevaluationpdfconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEvaluationPdfConfig>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveEvaluationPdfConfig>>>(),
});
export const config_evaluation_pdfRoute = makeRoute(
  "/ts/config_evaluation_pdf",
);

export const getNonPeriodicPaymentSchemeSpec = apiSpec({
  route: makeRoute("/ts/getnonperiodicpaymentscheme"),
  method: "POST",
  body: magicCodec<Parameters<typeof getNonPeriodicPaymentScheme>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof getNonPeriodicPaymentScheme>>>(),
});

export const saveOnlinePaymentFullSpec = apiSpec({
  route: makeRoute("/ts/saveonlinepaymentfull"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveOnlinePaymentFull>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveOnlinePaymentFull>>>(),
});

export const getPossibleInvoicesForOnlinePaymentSpec = apiSpec({
  route: makeRoute("/ts/getpossibleinvoicesforonlinepayment"),
  method: "POST",
  body: magicCodec<Parameters<typeof getPossibleInvoicesForOnlinePayment>[2]>(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getPossibleInvoicesForOnlinePayment>>
    >(),
});

export const deleteEmailTemplateSpec = apiSpec({
  route: makeRoute("/ts/deleteemailtemplate"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteEmailTemplate>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteEmailTemplate>>>(),
});

export const getComplementLessonsBySlotSpec = apiSpec({
  route: makeRoute("/ts/getcomplementlessonsbyslot"),
  method: "POST",
  body: magicCodec<Parameters<typeof getComplementLessonsBySlot>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getComplementLessonsBySlot>>>(),
});

export const queryEvaluationsMissingSpec = apiSpec({
  route: makeRoute("/ts/queryevaluationsmissing"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryEvaluationsMissing>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryEvaluationsMissing>>>(),
});
export const evaluation_missing_listRoute = makeRoute(
  "/ts/evaluation_missing_list",
);

export const reenrollment_batchesRoute = makeRoute("/ts/reenrollment_batches");

export const searchAuditLogOnStudentSpec = apiSpec({
  route: makeRoute("/ts/search_audit_log_on_student"),
  method: "POST",
  body: magicCodec<Parameters<typeof searchAuditLogOnStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof searchAuditLogOnStudent>>>(),
});
export const audit_log_overviewRoute = makeRoute("/ts/audit_log_overview");

export const deleteEvaluationSpec = apiSpec({
  route: makeRoute("/ts/deleteevaluation"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteEvaluation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteEvaluation>>>(),
});

export const payment_detailRoute = makeRoute(
  "/ts/online_payment_detail/{paymentproviderid:mollie_payment_id}",
  newtypeEncoders,
);

export const queryEvaluationsSpec = apiSpec({
  route: makeRoute("/ts/queryevaluations"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryEvaluations>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryEvaluations>>>(),
});
export const evaluation_listRoute = makeRoute("/ts/evaluation_list");

export const saveEvaluationTemplateSpec = apiSpec({
  route: makeRoute("/ts/saveevaluationtemplate"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEvaluationTemplate>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveEvaluationTemplate>>>(),
});
export const evaluation_templateRoute = makeRoute(
  "/ts/evaluation_template?{evaluation_template_id:evaluation_template_id}",
  newtypeEncoders,
);
export const evaluation_templates_listRoute = makeRoute(
  "/ts/evaluation_templates_list",
);

export const saveStudentAttachmentSpec = apiSpec({
  route: makeRoute("/ts/savestudentattachment"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentAttachment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentAttachment>>>(),
});

export const deleteStudentAttachmentSpec = apiSpec({
  route: makeRoute(
    "/ts/deletestudentattachment/{student_attachment_id:student_attachment_id}",
    newtypeEncoders,
  ),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteStudentAttachment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteStudentAttachment>>>(),
});
export const failed_enrollment_requestsRoute = makeRoute(
  "/ts/failed_enrollment_requests",
);

export const updateQuestionsSpec = apiSpec({
  route: makeRoute("/ts/updatequestions"),
  method: "POST",
  body: magicCodec<Parameters<typeof updateQuestions>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof updateQuestions>>>(),
});
export const lessongroup_questions_editRoute = makeRoute(
  "/ts/lessongroup_questions_edit/{lessongroupid:lessongroupid}/student/{studentid:studentid}/enrollment/{enrollmentid:enrollmentid}",
  newtypeEncoders,
);

export const savePaymentSchemeSpec = apiSpec({
  route: makeRoute("/ts/savepaymentscheme"),
  method: "POST",
  body: magicCodec<Parameters<typeof savePaymentScheme>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof savePaymentScheme>>>(),
});
export const payment_schemeRoute = makeRoute(
  "/ts/payment_schemes?{payment_scheme_id:payment_scheme_id}&{periodic:boolean}",
  newtypeEncoders,
);
export const payment_schemes_listRoute = makeRoute("/ts/payment_schemes_list");
export const lessongroup_questionsRoute = makeRoute(
  "/ts/lessongroup_questions/{lessongroupid:lessongroupid}?{orientation:orientation}",
  {
    lessongroupid: newtypeEncoder<lessongroupid>(),
    orientation: enumEncoder(["vertical" as const, "horizontal" as const]),
  },
);

export const deleteFromWaitinglistLessongroupSpec = apiSpec({
  route: makeRoute("/ts/deletefromwaitinglistlessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteFromWaitinglistLessongroup>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof deleteFromWaitinglistLessongroup>>>(),
});
export const waitinglist_admin_lessongroupRoute = makeRoute(
  "/ts/waitinglist_admin_lessongroup",
);

export const deleteFromWaitinglistSpec = apiSpec({
  route: makeRoute("/ts/deletefromwaitinglist"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteFromWaitinglist>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteFromWaitinglist>>>(),
});

export const saveWaitinglistNewStudentSpec = apiSpec({
  route: makeRoute("/ts/savewaitinglistnewstudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveWaitinglistNewStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveWaitinglistNewStudent>>>(),
});

export const saveWaitinglistExistingStudentSpec = apiSpec({
  route: makeRoute("/ts/savewaitinglistexistingstudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveWaitinglistExistingStudent>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof saveWaitinglistExistingStudent>>>(),
});
export const waitinglist_studentRoute = makeRoute(
  "/ts/waitinglist_student/{privateorgroup:privateorgroup}",
  {
    privateorgroup: makeEncoderFromCodec(
      c.oneOf([
        c.Codec.interface({
          tag: c.exactly("private"),
          instrumentid: newtypeCodec<instrumentid>(),
        }),
        c.Codec.interface({
          tag: c.exactly("lessongroup"),
          lessongroupid: newtypeCodec<lessongroupid>(),
        }),
      ]),
    ),
  },
);
export const waitinglist_student_successRoute = makeRoute(
  "/ts/waitinglist_student_success",
);
export const waitinglist_adminRoute = makeRoute("/ts/waitinglist_admin");

export const loadEnrollmentStatisticsSpec = apiSpec({
  route: makeRoute("/ts/loadenrollmentstatistics"),
  method: "POST",
  body: magicCodec<Parameters<typeof loadEnrollmentStatistics>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof loadEnrollmentStatistics>>>(),
});
export const statistics_enrollmentsRoute = makeRoute(
  "/ts/statistics_enrollments",
);

export const saveNewPasswordSpec = apiSpec({
  route: makeRoute("/ts/savenewpassword"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewPassword>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewPassword>>>(),
});
export const reset_passwordRoute = makeRoute("/ts/reset_password");

export const tryWithPasswordLoginSpec = apiSpec({
  route: makeRoute(
    "/ts/trywithpasswordlogin?{redirecturl:string}&{redirectdescription:string}",
  ),
  method: "POST",
  body: magicCodec<Parameters<typeof tryWithPasswordLogin>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof tryWithPasswordLogin>>>(),
});

const roleEncoder: Encoder<role> = {
  serialize: (s) => roleToString(s, { withSlash: true }),
  parse: (s) => {
    const role = stringToRole(s);
    if (role === null) {
      return Left(new Error("Failed to parse role"));
    } else {
      return Right(role);
    }
  },
};
export const tryPasswordlessLoginSpec = apiSpec({
  route: makeRoute(
    "/ts/trypasswordlesslogin?{redirecturl:string}&{redirectdescription:string}",
  ),
  method: "POST",
  body: magicCodec<Parameters<typeof tryPasswordlessLogin>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof tryPasswordlessLogin>>>(),
});
export const loginRoute = makeRoute(
  "/ts/login?{redirecturl:string}&{redirectdescription:string}&{logoutfirst:boolean}",
);
export const loginWithPasswordRoute = makeRoute(
  "/ts/login_with_password?{redirecturl:string}&{redirectdescription:string}",
);
export const loginPasswordlessRoute = makeRoute(
  "/ts/login_passwordless?{redirecturl:string}&{redirectdescription:string}",
);
export const loginWithDemoRoute = makeRoute(
  "/ts/login_with_demo/{email:string}?{redirecturl:string}&{redirectdescription:string}",
);

export const login_choose_roleRoute = makeRoute(
  "/ts/login_choose_role/{email:string}/{firstname:string}/{lastname:string}/{signature:string}/{createdOn:instant}/{role:role}?{redirecturl:string}&{redirectdescription:string}",
  {
    role: roleEncoder,
  },
);
export const login_passwordless_helpRoute = makeRoute(
  "/ts/login_passwordless/help?{redirecturl:string}&{redirectdescription:string}",
);
export const login_with_password_helpRoute = makeRoute(
  "/ts/login_with_password/help?{redirecturl:string}&{redirectdescription:string}",
);

export const loginPasswordlessEmailLinkRoute = makeRoute(
  "/ts/login_passwordless/emaillink/{email:string}/{secret:string}?{redirecturl:string}&{redirectdescription:string}",
);

export const savePeriodSpec = apiSpec({
  route: makeRoute("/ts/saveperiod"),
  method: "POST",
  body: magicCodec<Parameters<typeof savePeriod>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof savePeriod>>>(),
});

export const saveMoneybirdAdministrationSpec = apiSpec({
  route: makeRoute("/ts/savemoneybirdadministration"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveMoneybirdAdministration>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof saveMoneybirdAdministration>>>(),
});

export const saveMollieProfileSpec = apiSpec({
  route: makeRoute("/ts/savemollieprofile"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveMollieProfile>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveMollieProfile>>>(),
});

export const saveAlwaysShowWireInstructionsSpec = apiSpec({
  route: makeRoute("/ts/savealwaysshowwireinstructions"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveAlwaysShowWireInstructions>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof saveAlwaysShowWireInstructions>>>(),
});
export const config_mollieRoute = makeRoute("/ts/config_mollie");
export const config_accountingsoftwareRoute = makeRoute(
  "/ts/config_accountingsoftware",
);
export const redirectToOauthAuthorizeUrlRoute = makeRoute(
  "/ts/redirect_to_oauth_authorize/{target:target}",
  {
    target: enumEncoder(["mollie" as const, "moneybird" as const]),
  },
);

export const oauth_redirectRoute = makeRoute(
  "/ts/oauth_redirect/{target:target}",
  {
    target: enumEncoder(["mollie" as const, "moneybird" as const]),
  },
);

export const saveSchoolConfigSpec = apiSpec({
  route: makeRoute("/ts/saveschoolconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveSchoolConfig>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveSchoolConfig>>>(),
});
export const config_schoolconfigRoute = makeRoute("/ts/config_schoolconfig");
export const lessons_now_or_soonRoute = makeRoute(
  "/ts/lessons_now_or_soon/{noworsoon:noworsoon}",
  {
    noworsoon: enumEncoder(["now" as const, "soon" as const]),
  },
);

export const getLessonStatisticSpec = apiSpec({
  route: makeRoute("/ts/getlessonstatistic"),
  method: "POST",
  body: magicCodec<Parameters<typeof getLessonStatistic>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getLessonStatistic>>>(),
});
export const lesson_statisticsRoute = makeRoute("/ts/lesson_statistics");
export const profileRoute = makeRoute("/ts/profile");
export const saveEmailSettingsSpec = apiSpec({
  route: makeRoute("/ts/saveemailsettings"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEmailSettings>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveEmailSettings>>>(),
});
export const emailsettingsRoute = makeRoute(
  "/ts/emailsettings/{emailaddress:string}/{secret:string}",
);
export const emailsettingsNoSecretRoute = makeRoute(
  "/ts/emailsettings/{emailaddress:string}",
);
export const welcomeRoute = makeRoute("/ts/welcome");
export const help_startupRoute = makeRoute("/ts/help_startup");
export const help_lessonsRoute = makeRoute("/ts/help_lessons");
export const help_communicationRoute = makeRoute("/ts/help_communication");
export const help_advancedRoute = makeRoute("/ts/help_advanced");

export const setTeacherActiveSpec = apiSpec({
  route: makeRoute("/ts/setteacheractive"),
  method: "POST",
  body: magicCodec<Parameters<typeof setTeacherActive>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof setTeacherActive>>>(),
});

export const saveTeacherSpec = apiSpec({
  route: makeRoute("/ts/saveteacher"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveTeacher>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveTeacher>>>(),
});
export const teacher_editRoute = makeRoute(
  "/ts/teacher_edit?{internalteacherid:internalteacherid}",
  newtypeEncoders,
);

export const queryInvoicesToReimburseSpec = apiSpec({
  route: makeRoute("/ts/queryinvoicestoreimburse"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryInvoicesToReimburse>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryInvoicesToReimburse>>>(),
});
export const invoices_to_reimburseRoute = makeRoute(
  "/ts/invoices_to_reimburse",
);
export const student_advancedRoute = makeRoute("/ts/student_advanced");

export const getTeacherByIdSpec = apiSpec({
  route: makeRoute("/ts/getteacherbyid"),
  method: "POST",
  body: magicCodec<Parameters<typeof getTeacherById>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getTeacherById>>>(),
});

export const rollbackStudentAbsenceReplacementLessonSpec = apiSpec({
  route: makeRoute("/ts/rollbackstudentabsencereplacementlesson"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof rollbackStudentAbsenceReplacementLesson>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof rollbackStudentAbsenceReplacementLesson>>
    >(),
});

export const saveNewLessonSpec = apiSpec({
  route: makeRoute("/ts/savenewlesson"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewLesson>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewLesson>>>(),
});

export const deleteLessonSpec = apiSpec({
  route: makeRoute("/ts/deletelesson"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteLesson>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteLesson>>>(),
});

export const saveStudentAbsenceCommentsSpec = apiSpec({
  route: makeRoute("/ts/savestudentabsencecomments"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentAbsenceComments>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentAbsenceComments>>>(),
});
export const lessongroup_lessondataRoute = makeRoute(
  "/ts/lessongroup_lessondata/{lessongroupid:lessongroupid}?{selecteddate:date}&{monthsinfuture:number}",
  newtypeEncoders,
);

export const finalizeEvaluationSpec = apiSpec({
  route: makeRoute("/ts/finalizeevaluation"),
  method: "POST",
  body: magicCodec<Parameters<typeof finalizeEvaluation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof finalizeEvaluation>>>(),
});

export const saveEvaluationSpec = apiSpec({
  route: makeRoute("/ts/saveevaluation"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEvaluation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveEvaluation>>>(),
});
export const evaluation_editRoute = makeRoute(
  "/ts/evaluation_edit/{enrollmentid:enrollmentid}?{id:evaluationid}",
  newtypeEncoders,
);

export const deleteSiteSpec = apiSpec({
  route: makeRoute("/ts/deletesite"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteSite>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteSite>>>(),
});

export const saveSiteSpec = apiSpec({
  route: makeRoute("/ts/savesite"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveSite>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveSite>>>(),
});
export const site_editRoute = makeRoute(
  "/ts/site_edit?{siteid:siteid}",
  newtypeEncoders,
);
export const lessongroup_generalRoute = makeRoute(
  "/ts/lessongroup_general/{lessongroupid:lessongroupid}",
  newtypeEncoders,
);
export const student_listRoute = makeRoute("/ts/student_list");

export const deleteRoomSpec = apiSpec({
  route: makeRoute("/ts/deleteroom"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteRoom>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteRoom>>>(),
});

export const saveRoomSpec = apiSpec({
  route: makeRoute("/ts/saveroom"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveRoom>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveRoom>>>(),
});
export const roomeditRoute = makeRoute(
  "/ts/roomedit?{roomid:roomid}",
  newtypeEncoders,
);

export const downloadTenantInvoiceRoute = makeRoute(
  "/ts/downloadtenantinvoice/{id:tenantinvoiceid}",
  newtypeEncoders,
);
export const tenantinvoicesRoute = makeRoute("/ts/tenantinvoices");

export const deleteFormulaSpec = apiSpec({
  route: makeRoute("/ts/deleteformula"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteFormula>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteFormula>>>(),
});

export const deleteInstrumentSpec = apiSpec({
  route: makeRoute("/ts/deleteinstrument"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteInstrument>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteInstrument>>>(),
});

export const saveInstrumentSpec = apiSpec({
  route: makeRoute("/ts/saveinstrument"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveInstrument>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveInstrument>>>(),
});

export const config_instrumentRoute = makeRoute(
  "/ts/config_instruments?{id:instrumentid}",
  newtypeEncoders,
);
export const config_instrumentslistRoute = makeRoute(
  "/ts/config_instruments_list",
);

export const deleteAdminAccountSpec = apiSpec({
  route: makeRoute("/ts/deleteadminaccount"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteAdminAccount>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteAdminAccount>>>(),
});

export const saveNewAdminAccountSpec = apiSpec({
  route: makeRoute("/ts/savenewadminaccount"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewAdminAccount>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewAdminAccount>>>(),
});
export const adminaccountsEditRoute = makeRoute(
  "/ts/adminaccounts/{email:string}",
);
export const adminaccountsNewRoute = makeRoute("/ts/adminaccounts/new");
export const adminaccountsRoute = makeRoute("/ts/adminaccounts");

export const saveStudentSpec = apiSpec({
  route: makeRoute("/ts/savestudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudent>>>(),
});
export const studenteditRoute = makeRoute(
  "/ts/studentedit/{studentid:studentid}",
  newtypeEncoders,
);
export const studentnewRoute = makeRoute("/ts/studentnew");

export const getStoppingEnrollmentPeriodfeeStatusSpec = apiSpec({
  route: makeRoute("/ts/stoppingenrollmentperiodfeestatus"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof getStoppingEnrollmentPeriodfeeStatus>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getStoppingEnrollmentPeriodfeeStatus>>
    >(),
});
export const doSearchSpec = apiSpec({
  route: makeRoute("/ts/dosearch"),
  method: "POST",
  body: magicCodec<Parameters<typeof doSearch>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof doSearch>>>(),
});
export const searchRoute = makeRoute("/ts/search?{searchstring:string}");

export const switchPaymentSchemeSpec = apiSpec({
  route: makeRoute("/ts/switchpaymentscheme"),
  method: "POST",
  body: magicCodec<Parameters<typeof switchPaymentScheme>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof switchPaymentScheme>>>(),
});
export const config_periods_listRoute = makeRoute("/ts/periodsconfig_list");
export const config_periodsRoute = makeRoute(
  "/ts/periodsconfig?{periodid:periodid}",
  newtypeEncoders,
);
export const approvals_listRoute = makeRoute("/ts/approvals_list");

export const removeAbsenceFromListSpec = apiSpec({
  route: makeRoute("/ts/removeabsencefromlist"),
  method: "POST",
  body: magicCodec<Parameters<typeof removeAbsenceFromList>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof removeAbsenceFromList>>>(),
});
export const absences_to_approve_listRoute = makeRoute(
  "/ts/absences_to_approve_list",
  newtypeEncoders,
);
export const sites_listRoute = makeRoute("/ts/sites_list");
export const room_listRoute = makeRoute("/ts/room_list");
export const teacher_listRoute = makeRoute(
  "/ts/teacher_list?{include_external_and_deactivated:boolean}",
);

export const reporterrorSpec = apiSpec({
  route: makeRoute("/ts/reporterror"),
  method: "POST",
  body: magicCodec<Parameters<typeof reporterror>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof reporterror>>>(),
});

export const sendPaymentRemindersAndDirectDebitsPeriodicSpec = apiSpec({
  route: makeRoute("/ts/sendpaymentremindersperiodic"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof sendPaymentRemindersAndDirectDebitsPeriodic>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof sendPaymentRemindersAndDirectDebitsPeriodic>>
    >(),
});

export const sendPaymentRemindersNonPeriodicSpec = apiSpec({
  route: makeRoute("/ts/sendpaymentremindersnonperiodic"),
  method: "POST",
  body: magicCodec<Parameters<typeof sendPaymentRemindersNonPeriodic>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof sendPaymentRemindersNonPeriodic>>>(),
});

export const queryPeriodPaymentsStatisticSpec = apiSpec({
  route: makeRoute("/ts/queryperiodpaymentsstatistic"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryPeriodPaymentsStatistic>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof queryPeriodPaymentsStatistic>>>(),
});
export const payments_overview_periodicRoute = makeRoute(
  "/ts/payments_overview_periodic",
);

export const queryNonPeriodicPaymentsStatisticSpec = apiSpec({
  route: makeRoute("/ts/querynonperiodicpaymentsstatistic"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryNonPeriodicPaymentsStatistic>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof queryNonPeriodicPaymentsStatistic>>>(),
});
export const payments_overview_non_periodicRoute = makeRoute(
  "/ts/payments_overview_non_periodic",
);

export const queryInvoiceStatisticsSpec = apiSpec({
  route: makeRoute("/ts/queryinvoicestatistics"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryInvoiceStatistics>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryInvoiceStatistics>>>(),
});
export const finance_invoices_statisticsRoute = makeRoute(
  "/ts/finance_invoices",
);

export const doDeleteStudentSpec = apiSpec({
  route: makeRoute("/ts/dodeletestudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof doDeleteStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof doDeleteStudent>>>(),
});
export const deletestudentRoute = makeRoute(
  "/ts/deletestudent/{studentid:studentid}",
  newtypeEncoders,
);
export const studentOverviewRoute = makeRoute(
  "/ts/studentoverview/{studentid:studentid}",
  newtypeEncoders,
);
export const downloadStudentAttachmentSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadstudentattachment/{student_attachment_id:student_attachment_id}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});
export const student_financialRoute = makeRoute("/ts/student_financial");
export const mailgun_receiveRoute = makeRoute("/ts/mailgun_receive");

const paymentFlowTargetEncoder = makeEncoderFromSum({
  enrollmentrequest: newtypeEncoder<enrollmentrequestid>(),
  expectedpayments: makeEncoderFromCodec(
    c.Codec.interface({
      expectedpaymentids: c.array(newtypeCodec<expectedpaymentid>()),
    }),
  ),
});
export const paymentFlowStudentPaymentHookRoute = makeRoute(
  "/ts/student_payment_hook",
);
export const paymentFlowAfterPaymentRoute = makeRoute(
  "/ts/after_payment/{target:target}",
  {
    target: paymentFlowTargetEncoder,
  },
);
export const startPaymentFlowInvoiceRoute = makeRoute(
  "/ts/startpaymentflow/{target:target}/{method:payment_method}",
  {
    target: paymentFlowTargetEncoder,
    payment_method: newtypeEncoderString<mollie_payment_method>(),
  },
);

export const payEnrollmentrequestRoute = makeRoute(
  "/ts/payenrollmentrequest/{enrollmentrequestid:enrollmentrequestid}",
  newtypeEncoders,
);

export const payExpectedPaymentsRoute = makeRoute(
  "/ts/payexpectedpayments/{expectedpayments:expectedpayments}",
  {
    expectedpayments: makeEncoderFromCodec(
      c.Codec.interface({
        expectedpaymentids: c.array(newtypeCodec<expectedpaymentid>()),
      }),
    ),
  },
);

// In email!
export const payInvoiceDeprecatedRoute = makeRoute(
  "/ts/payinvoice/{target:target}",
  {
    target: paymentFlowTargetEncoder,
  },
);
// In Email! (sometimes via redirect)
export const payEnrollmentRoute = makeRoute(
  "/ts/payenrollment/{enrollmentid:enrollmentid}",
  newtypeEncoders,
);

export const saveNewPublicEnrollmentSpec = apiSpec({
  route: makeRoute("/ts/savenewpublicenrollmentnewstudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewPublicEnrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewPublicEnrollment>>>(),
});

// used as public link
export const lessongroup_publicRoute = makeRoute(
  "/lessongroup_public/{lessongroupid:lessongroupid}",
  newtypeEncoders,
);

export const public_new_enrollment_lessongroup_oldRoute = makeRoute(
  "/ts/public_new_enrollment/lessongroup/{lessongroupid:lessongroupid}",
  newtypeEncoders,
);

export const public_new_enrollment_lessongroupRoute = makeRoute(
  "/ts/public_new_enrollment/lessongroup/{lessongroupid:lessongroupid}/{trial_lesson:boolean}",
  newtypeEncoders,
);
export const public_new_enrollment_privateRoute = makeRoute(
  "/ts/public_new_enrollment/private/{instrumentid:instrumentid}/{formulaid:formulaid}/{startdate:date}/{starttime:time}?{internalteacherid:internalteacherid}",
  {
    ...newtypeEncoders,
    time: timeEncoder,
  },
);
export const public_new_enrollment_confirmationRoute = makeRoute(
  "/ts/public_new_enrollment/confirm/{enrollmentrequestid:enrollmentrequestid}",
  newtypeEncoders,
);

export const public_new_enrollment_confirmation_email_sentRoute = makeRoute(
  "/ts/public_new_enrollment/confirmation_email_sent",
);

export const markPaymentPaidSpec = apiSpec({
  route: makeRoute("/ts/markinvoicepaid"),
  method: "POST",
  body: magicCodec<Parameters<typeof markPaymentPaid>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof markPaymentPaid>>>(),
});

export const saveInvoiceFullSpec = apiSpec({
  route: makeRoute("/ts/saveinvoicefull"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveInvoiceFull>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveInvoiceFull>>>(),
});

export const downloadInvoicePdfSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadinvoicepdf/{invoiceid:invoiceid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});

export const invoicedetailsRoute = makeRoute(
  "/ts/invoicedetails/{invoiceid:invoiceid}/edit/{edit:boolean}",
  newtypeEncoders,
);

export const saveEachPeriodFinancialConfigSpec = apiSpec({
  route: makeRoute("/ts/saveeachperiodfinancialconfig"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEachPeriodFinancialConfig>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof saveEachPeriodFinancialConfig>>>(),
});
export const config_financialRoute = makeRoute("/ts/config_financial");

export const saveFormulaSpec = apiSpec({
  route: makeRoute("/ts/saveformula"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveFormula>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveFormula>>>(),
});
export const config_formulasRoute = makeRoute(
  "/ts/config_formulas?{formulaid:formulaid}&{copy:boolean}",
  newtypeEncoders,
);
export const config_formulas_listRoute = makeRoute("/ts/config_formulas_list");

export const saveStudentProfileSpec = apiSpec({
  route: makeRoute("/ts/savestudentprofile"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentProfile>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentProfile>>>(),
});

export const logoutRoute = makeRoute("/ts/logout?{redirecturl:string}");

export const student_profileRoute = makeRoute("/ts/student_profile");

export const downloadEventAttachmentSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadeventattachment/{eventid:eventid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});
export const yearcalendarRoute = makeRoute(
  "/ts/yearcalendar?{periodid:periodid}",
  newtypeEncoders,
);
export const yearcalendarTeacherRoute = makeRoute(
  "/ts/yearcalendar_teacher/{teacherid:teacherid}?{periodid:periodid}",
  newtypeEncoders,
);

export const downloadEvaluationPdfSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadevaluationpdf/{evaluationid:evaluationid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});

export const downloadStudentAbsenceFileSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadstudentabsencefile/{lessonid:lessonid}/{studentid:studentid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});

export const uploadStudentAbsenceFileSpec = apiSpec({
  route: makeRoute("/ts/uploadstudentabsencefile"),
  method: "POST",
  body: magicCodec<Parameters<typeof uploadStudentAbsenceFile>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof uploadStudentAbsenceFile>>>(),
});

export const getEnrollmentDetailsForStudentStartSpec = apiSpec({
  route: makeRoute(
    "/ts/getenrollmentdetailsforstudentstart/{lessongroupid:lessongroupid}/{enrollmentid:enrollmentid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getEnrollmentDetailsForStudentStart>>
    >(),
});
export const student_startRoute = makeRoute("/ts/student_start");

export const removeSmsSpec = apiSpec({
  route: makeRoute("/ts/removesms"),
  method: "POST",
  body: magicCodec<Parameters<typeof removeSms>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof removeSms>>>(),
});

export const rejectReenrollmentSpec = apiSpec({
  route: makeRoute("/ts/rejectreenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof rejectReenrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof rejectReenrollment>>>(),
});

export const confirmReenrollmentSpec = apiSpec({
  route: makeRoute("/ts/confirmreenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof confirmReenrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof confirmReenrollment>>>(),
});
export const reenroll_invitationRoute = makeRoute(
  "/ts/reenroll_invitation/{id:enrollmentrequestid}",
  newtypeEncoders,
);

export const execReenrollmentsSpec = apiSpec({
  route: makeRoute("/ts/execreenrollments"),
  method: "POST",
  body: magicCodec<Parameters<typeof execReenrollments>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof execReenrollments>>>(),
});

export const queryReenrollmentsSpec = apiSpec({
  route: makeRoute("/ts/queryreenrollments"),
  method: "POST",
  body: magicCodec<Parameters<typeof queryReenrollments>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof queryReenrollments>>>(),
});
export const reenrollmentsInsidePeriodRoute = makeRoute(
  "/ts/reenrollments/insideperiod",
);
export const reenrollmentsEndOfPeriodRoute = makeRoute(
  "/ts/reenrollments/newperiod",
);
export const loadRoomplanningSlotsSpec = apiSpec({
  route: makeRoute("/ts/loadroomplanningslots"),
  method: "POST",
  body: magicCodec<Parameters<typeof loadRoomplanningSlots>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof loadRoomplanningSlots>>>(),
});

export const setRoomplanningFilterCookieSpec = apiSpec({
  route: makeRoute("/ts/setroomplanningfiltercookie"),
  method: "POST",
  body: magicCodec<Parameters<typeof setRoomplanningFilterCookie>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof setRoomplanningFilterCookie>>>(),
});
export const roomplanningRoute = makeRoute(
  "/ts/roomplanning?{date:date}&{roomid:roomid}",
  newtypeEncoders,
);

export const markAsUnreadSpec = apiSpec({
  route: makeRoute("/ts/markasunread"),
  method: "POST",
  body: magicCodec<Parameters<typeof markAsUnread>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof markAsUnread>>>(),
});

export const setStudentPresenceSpec = apiSpec({
  route: makeRoute("/ts/setstudentpresence"),
  method: "POST",
  body: magicCodec<Parameters<typeof setStudentPresence>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof setStudentPresence>>>(),
});

export const getNotesForLessonSpec = apiSpec({
  route: makeRoute("/ts/getnotesforlesson"),
  method: "POST",
  body: magicCodec<Parameters<typeof getNotesForLesson>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getNotesForLesson>>>(),
});
export const teacher_lessonrosterRoute = makeRoute(
  "/ts/teacher_lessonroster/{date:date}?{teacherid:teacherid}&{mode:dayorweek}",
  {
    teacherid: newtypeEncoder<teacherid>(),
    dayorweek: enumEncoder(["day" as const, "week" as const]),
  },
);
export const teacher_studentsRoute = makeRoute(
  "/ts/teacher_students?{teacherid:teacherid}&{include_older:boolean}",
  newtypeEncoders,
);

export const getPeriodfeeForStudentSpec = apiSpec({
  route: makeRoute("/ts/getperiodfeetoadd"),
  method: "POST",
  body: magicCodec<Parameters<typeof getPeriodfeeForStudent>[1]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getPeriodfeeForStudent>>>(),
});

export const getPossibleTeachersForEnrollmentRequestSpec = apiSpec({
  route: makeRoute("/ts/getpossibleteachersforenrollmentrequest"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof getPossibleTeachersForEnrollmentRequest>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getPossibleTeachersForEnrollmentRequest>>
    >(),
});

export const approvePrivateLessonEnrollmentRequestSpec = apiSpec({
  route: makeRoute("/ts/approveprivatelessonenrollmentrequest"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof approvePrivateLessonEnrollmentRequest>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof approvePrivateLessonEnrollmentRequest>>
    >(),
});

export const disproveEnrollmentRequestSpec = apiSpec({
  route: makeRoute("/ts/disproveenrollmentrequest"),
  method: "POST",
  body: magicCodec<Parameters<typeof disproveEnrollmentRequest>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof disproveEnrollmentRequest>>>(),
});

export const approveGroupLessonEnrollmentRequestSpec = apiSpec({
  route: makeRoute("/ts/approvegrouplessonenrollmentrequest"),
  method: "POST",
  body: magicCodec<Parameters<typeof approveEnrollmentRequestWithLg>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof approveEnrollmentRequestWithLg>>>(),
});

export const enrollment_requestRoute = makeRoute(
  "/ts/approve_enrollment_request/{enrollmentrequestid:enrollmentrequestid}",
  newtypeEncoders,
);

export const cancelTeacherAbsenceSpec = apiSpec({
  route: makeRoute("/ts/cancelteacherabsence"),
  method: "POST",
  body: magicCodec<Parameters<typeof cancelTeacherAbsence>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof cancelTeacherAbsence>>>(),
});

export const calculateCancelTeacherabsenceActionsSpec = apiSpec({
  route: makeRoute("/ts/calculatecancelteacherabsenceactions"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof calculateCancelTeacherabsenceActions>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof calculateCancelTeacherabsenceActions>>
    >(),
});

export const saveTeacherAbsenceSpec = apiSpec({
  route: makeRoute("/ts/saveteacherabsence"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveTeacherAbsence>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveTeacherAbsence>>>(),
});

export const getCatchupLessonDatesSpec = apiSpec({
  route: makeRoute("/ts/getcatchuplessondates"),
  method: "POST",
  body: magicCodec<Parameters<typeof getCatchupLessonDates>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getCatchupLessonDates>>>(),
});

export const getPossibleExternalReplacementsSpec = apiSpec({
  route: makeRoute("/ts/getpossibleexternalreplacements"),
  method: "POST",
  body: magicCodec<Parameters<typeof getPossibleExternalReplacements>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof getPossibleExternalReplacements>>>(),
});

export const getPossibleInternalReplacementsSpec = apiSpec({
  route: makeRoute("/ts/getpossibleinternalreplacements"),
  method: "POST",
  body: magicCodec<Parameters<typeof getPossibleInternalReplacements>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof getPossibleInternalReplacements>>>(),
});

export const deleteTeachingPeriodSpec = apiSpec({
  route: makeRoute("/ts/deleteteachingperiod"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteTeachingPeriod>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteTeachingPeriod>>>(),
});

export const saveTeachingperiodSpec = apiSpec({
  route: makeRoute("/ts/saveteachingperiod"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveTeachingperiod>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveTeachingperiod>>>(),
});

export const getAvailableRoomsForTeachingPeriodSpec = apiSpec({
  route: makeRoute("/ts/getavailableroomsforteachingperiod"),
  method: "POST",
  body: magicCodec<Parameters<typeof getAvailableRoomsForTeachingPeriod>[2]>(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getAvailableRoomsForTeachingPeriod>>
    >(),
});

export const stopGroupEnrollmentSpec = apiSpec({
  route: makeRoute("/ts/stopgroupenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof stopEnrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof stopEnrollment>>>(),
});

export const stopPrivateLessongroupSpec = apiSpec({
  route: makeRoute("/ts/stopprivatelessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof stopPrivateLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof stopPrivateLessongroup>>>(),
});

export const stopGroupLessongroupSpec = apiSpec({
  route: makeRoute("/ts/stopgrouplessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof stopLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof stopLessongroup>>>(),
});

export const getAllEnrollmentsForLessongroupSpec = apiSpec({
  route: makeRoute("/ts/getallenrollmentsforlessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof getAllEnrollmentsForLessongroup>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof getAllEnrollmentsForLessongroup>>>(),
});

export const moveEnrollmentSpec = apiSpec({
  route: makeRoute("/ts/moveenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof moveEnrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof moveEnrollment>>>(),
});

export const moveLessonOrLessongroupSpec = apiSpec({
  route: makeRoute("/ts/movelessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof moveLessonOrLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof moveLessonOrLessongroup>>>(),
});

export const getFormulaChangeActionsSpec = apiSpec({
  route: makeRoute("/ts/getformulachangeactions"),
  method: "POST",
  body: magicCodec<Parameters<typeof getFormulaChangeActions>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getFormulaChangeActions>>>(),
});

export const changeLessongroupSpec = apiSpec({
  route: makeRoute("/ts/changelessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof doChangeLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof doChangeLessongroup>>>(),
});

export const getActiveLessonsForLessongroupSpec = apiSpec({
  route: makeRoute("/ts/getlessonsforlessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof getActiveLessonsForLessongroup>[2]>(),
  returns:
    magicCodec<Awaited<ReturnType<typeof getActiveLessonsForLessongroup>>>(),
});

export const saveNewEnrollmentSpec = apiSpec({
  route: makeRoute("/ts/savenewenrollment"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewEnrollment>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewEnrollment>>>(),
});

export const getInvoiceLinesForLessongroupSpec = apiSpec({
  route: makeRoute("/ts/getinvoicelinesforlessongroup"),
  method: "POST",
  body: magicCodec<
    Parameters<typeof getInvoiceLinesForLessongroupHandler>[2]
  >(),
  returns:
    magicCodec<
      Awaited<ReturnType<typeof getInvoiceLinesForLessongroupHandler>>
    >(),
});

export const saveNewPrivateLessongroupSpec = apiSpec({
  route: makeRoute("/ts/savenewprivatelessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewPrivateLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewPrivateLessongroup>>>(),
});

export const saveNewGrouplessonSpec = apiSpec({
  route: makeRoute("/ts/savenewlessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveNewGrouplesson>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveNewGrouplesson>>>(),
});

export const searchStudentSpec = apiSpec({
  route: makeRoute("/ts/searchstudent"),
  method: "POST",
  body: magicCodec<Parameters<typeof searchStudent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof searchStudent>>>(),
});

export const calculateRoomSuggestionsSpec = apiSpec({
  route: makeRoute("/ts/calculateroomsuggestions"),
  method: "POST",
  body: magicCodec<Parameters<typeof calculateRoomSuggestions>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof calculateRoomSuggestions>>>(),
});

export const loadSlotsSpec = apiSpec({
  route: makeRoute("/ts/loadslots"),
  method: "POST",
  body: magicCodec<Parameters<typeof loadSlots>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof loadSlots>>>(),
});

export const setPlanningFilterCookieSpec = apiSpec({
  route: makeRoute("/ts/setfilter"),
  method: "POST",
  body: magicCodec<Parameters<typeof setPlanningFilter>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof setPlanningFilter>>>(),
});

export const setPlanningDisplayModeCookieSpec = apiSpec({
  route: makeRoute("/ts/setdisplaymode"),
  method: "POST",
  body: magicCodec<Parameters<typeof setPlanningDisplayMode>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof setPlanningDisplayMode>>>(),
});
export const planningRoute = makeRoute(
  "/ts/planning?{date:date}&{teacher:internalteacherid}&{time:time}",
  {
    internalteacherid: newtypeEncoder<internalteacherid>(),
    time: timeEncoder,
  },
);

export const getNotesForLessongroupSpec = apiSpec({
  route: makeRoute("/ts/getnotesforlessongroup"),
  method: "POST",
  body: magicCodec<Parameters<typeof getNotesForLessongroup>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getNotesForLessongroup>>>(),
});

export const downloadFileNoteSpec = apiSpec({
  route: makeRoute(
    "/ts/downloadfilenote/{filenoteid:filenoteid}/{storagefileid:b2fileid}",
    newtypeEncoders,
  ),
  method: "GET",
  body: null,
  returns: c.nullType,
});

export const deleteFileNoteSpec = apiSpec({
  route: makeRoute("/ts/deletefilenote"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteFileNote>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteFileNote>>>(),
});

export const deleteTextNoteSpec = apiSpec({
  route: makeRoute("/ts/deletetextnote"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteTextNote>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteTextNote>>>(),
});

export const saveFileNoteSpec = apiSpec({
  route: makeRoute("/ts/savefilenote"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveFileNote>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveFileNote>>>(),
});

export const saveTextNoteSpec = apiSpec({
  route: makeRoute("/ts/savetextnote"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveTextNote>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveTextNote>>>(),
});
export const notesRoute = makeRoute("/ts/notesfull/{selected:selected}", {
  selected: makeEncoderFromSum({
    none: builtinEncoders.string,
    lessongroup: newtypeEncoder<lessongroupid>(),
  }),
});

export const sendMessageSpec = apiSpec({
  route: makeRoute("/ts/sendmessage"),
  method: "POST",
  body: magicCodec<Parameters<typeof sendMessage>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof sendMessage>>>(),
});

export const searchCounterpartySpec = apiSpec({
  route: makeRoute("/ts/searchcounterparty"),
  method: "POST",
  body: magicCodec<Parameters<typeof searchCounterparty>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof searchCounterparty>>>(),
});

export const getMessagesForConversationSpec = apiSpec({
  route: makeRoute("/ts/getmessagesforconversation"),
  method: "POST",
  body: magicCodec<Parameters<typeof getMessagesForConversation>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getMessagesForConversation>>>(),
});
export const messagesMainRoute = makeRoute("/ts/messages");
export const messagesRoute = makeRoute(
  "/ts/messages/{showall:boolean}/{selected:selected}",
  {
    selected: makeEncoderFromSum({
      none: builtinEncoders.string,
      teacher: newtypeEncoder<teacherid>(),
      student: newtypeEncoder<studentid>(),
    }),
  },
);
export const messagesemailsRoute = makeRoute(
  "/ts/messages/email/{messageid:messageid}",
  newtypeEncoders,
);

export const resendEmailSpec = apiSpec({
  route: makeRoute("/ts/resendemail"),
  method: "POST",
  body: magicCodec<Parameters<typeof resendEmail>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof resendEmail>>>(),
});

export const removeEmailSpec = apiSpec({
  route: makeRoute("/ts/removeemail"),
  method: "POST",
  body: magicCodec<Parameters<typeof removeEmail>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof removeEmail>>>(),
});
export const erroremailsRoute = makeRoute("/ts/erroremails");
export const erroremailsDetailRoute = makeRoute(
  "/ts/erroremails/{emailid:emailid}",
  newtypeEncoders,
);

export const deleteEventSpec = apiSpec({
  route: makeRoute("/ts/deleteevent"),
  method: "POST",
  body: magicCodec<Parameters<typeof deleteEvent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof deleteEvent>>>(),
});

export const saveEventSpec = apiSpec({
  route: makeRoute("/ts/saveevent"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveEvent>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveEvent>>>(),
});
export const editeventRoute = makeRoute(
  "/ts/event/{eventid:eventid}",
  newtypeEncoders,
);
export const neweventRoute = makeRoute("/ts/newevent/{date:date}");

export const saveStudentProfilesMessageSpec = apiSpec({
  route: makeRoute("/ts/savestudentprofilesmessage"),
  method: "POST",
  body: magicCodec<Parameters<typeof saveStudentProfilesMessage>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof saveStudentProfilesMessage>>>(),
});
export const editmessageonstudentprofilesRoute = makeRoute(
  "/ts/editmessageonstudentprofiles",
);

export const sendGroupMessageSpec = apiSpec({
  route: makeRoute("/ts/sendgroupmessage"),
  method: "POST",
  body: magicCodec<Parameters<typeof sendGroupMessage>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof sendGroupMessage>>>(),
});

export const getFilteredStudentsSpec = apiSpec({
  route: makeRoute("/ts/getfilteredstudents"),
  method: "POST",
  body: magicCodec<Parameters<typeof getFilteredStudents>[2]>(),
  returns: magicCodec<Awaited<ReturnType<typeof getFilteredStudents>>>(),
});

export const groupmessageRoute = makeRoute("/ts/groupmessage");
export const groupmessageTeachersRoute = makeRoute("/ts/groupmessage/teachers");

export function newtypeCodec<T extends number>(): c.Codec<T> {
  return c.number as unknown as c.Codec<T>;
}

export function newtypeEncoder<T extends number>(): Encoder<T> {
  return {
    parse: function parse(s: string): Either<Error, T> {
      const parsed = parseIntSafe(s);
      if (parsed) {
        return Right(parsed as T);
      } else {
        return Left(new Error("Failed to parse int"));
      }
    },
    serialize: (t: T) => t.toString(),
  };
}
export function newtypeEncoderString<T extends string>(): Encoder<T> {
  return {
    parse: function parse(s: string): Either<Error, T> {
      if (isString(s)) {
        return Right(s as T);
      } else {
        return Left(new Error("Not a string"));
      }
    },
    serialize: (t: T) => t,
  };
}
export function enumEncoder<T extends string>(values: T[]) {
  return {
    parse: function parse(s: string): Either<Error, T> {
      // eslint-disable-next-line
      const found = values.find((v) => v === s);
      if (found) {
        return Right(found);
      } else {
        return Left(new Error("Failed to parse string enum"));
      }
    },
    serialize: (t: T) => t.toString(),
  };
}

// Codecs are just a distraction/annoyance/perfdrain in this codebase:
// We only communicate between our own server and client,
// and they are always in sync because of typescript
// This is also not an SPA, so server and client are pretty much always
// in sync
/* eslint-disable */
export function magicCodec<T>(): c.Codec<T> {
  const c: c.Codec<T> = {
    decode: (d) =>
      Right(deserializeProps(d as unknown as string)) as Either<string, T>,
    encode: (p: T) =>
      runWithCustomSerializers(() =>
        p === undefined
          ? null /* JSON can't stringify undefined */
          : JSON.stringify(p),
      ) as any,
    unsafeDecode: (b) => b as T,
    schema: () => null as any,
  };
  return c;
}
/* eslint-enable */
